import * as yup from 'yup';
import { createCustomQuestionResponse } from '../api/CustomQuestion';
import { Audience } from '../api/CustomQuestion/types/Audience';
import {
  CustomQuestion,
  RespondersCustomQuestions,
} from '../api/CustomQuestion/types/CustomQuestion';
import { CustomQuestionType } from '../api/CustomQuestion/types/Type';
import { TenantId } from '../api/tenants/Tenant';
import { lengthField } from './form';
import { length } from './wysiwyg';

export const customFieldsMaxLength = {
  [CustomQuestionType.PARAGRAPH]: lengthField.wysiwygDisplayLimit,
  [CustomQuestionType.TEXT]: 1024,
};

export const customQuestionFileExtensions = [
  '.pdf',
  '.ppt',
  '.pptx',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.txt',
  '.png',
  '.jpeg',
  '.jpg',
  '.gif',
];

/**
 * Get MIME content type from file extension
 */
export const getContentTypeFromExtension = (extension: string): string => {
  const contentTypeMap: Record<string, string> = {
    pdf: 'application/pdf',
    ppt: 'application/vnd.ms-powerpoint',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    txt: 'text/plain',
    png: 'image/png',
    jpeg: 'image/jpeg',
    jpg: 'image/jpeg',
    gif: 'image/gif',
  };
  return contentTypeMap[extension.toLowerCase()] || 'application/octet-stream';
};

/**
 * Extract file path from attachFileToCustomQuestion response
 * Handles both string response and nested object structures
 */
export const extractFilePathFromResponse = (
  response: string | { filePath?: string | { filePath?: string } },
): string => {
  if (typeof response === 'string') {
    return response;
  }
  if (response && typeof response === 'object') {
    const filePath = response.filePath;
    if (typeof filePath === 'string') {
      return filePath;
    }
    if (filePath && typeof filePath === 'object' && filePath.filePath) {
      return filePath.filePath;
    }
  }
  return '';
};

/**
 * Create attachment metadata object for backend
 */
export const createAttachmentMetadata = (
  filePath: string,
  fileName: string,
  fileSize: number,
  contentType: string,
): string => {
  const now = new Date();
  const uploadDate = now.toISOString().split('.')[0]; // Remove milliseconds

  const attachmentMetadata = {
    filePath,
    fileName,
    fileSize,
    contentType,
    uploadDate,
  };

  return JSON.stringify(attachmentMetadata);
};

export const getCustomQuestionsDefaultValues = (
  questions: CustomQuestion[],
) => {
  return questions.reduce((acc, question) => {
    switch (question.type) {
      case CustomQuestionType.SINGLE_CHOICE:
        const singleChoiceValue =
          question?.options?.find(
            (option) => option?.optionResponse?.value === 'selected',
          )?.id || '';
        return {
          ...acc,
          [question.id]: singleChoiceValue,
        };
      case CustomQuestionType.MULTIPLE_CHOICE:
        const multipleChoiceValue = question?.options?.length
          ? question.options
              .filter((option) => option.optionResponse?.value === 'selected')
              .map((option) => option.id)
              .join(',')
          : '';
        return {
          ...acc,
          [question.id]: multipleChoiceValue,
        };
      case CustomQuestionType.ATTACHMENT:
        return {
          ...acc,
          [question.id]: question?.response?.value || '',
        };
      case CustomQuestionType.TEXT:
      case CustomQuestionType.PARAGRAPH:
      case CustomQuestionType.NUMBER:
      default:
        return {
          ...acc,
          [question.id]: question?.response?.value || '',
        };
    }
  }, {});
};

export const getCustomQuestionsValidationSchema = (
  questions?: CustomQuestion[],
) => {
  if (questions?.length) {
    return yup.object().shape(
      questions.reduce((acc, question) => {
        const { id, type, isMandatory } = question;
        const fieldValidation = getCustomQuestionsValidation(type, isMandatory);
        return {
          ...acc,
          [id]: fieldValidation,
        };
      }, {}),
    );
  }
  return undefined;
};

export const getCustomQuestionsValidation = (
  type: CustomQuestionType,
  isMandatory: boolean,
) => {
  switch (type) {
    case CustomQuestionType.PARAGRAPH:
      return isMandatory
        ? yup
            .string()
            .max(lengthField.wysiwygMaxLength)
            .test(
              'max-len-text',
              `Max ${lengthField.wysiwygDisplayLimit} characters`,
              (val) => !val || length(val) <= lengthField.wysiwygDisplayLimit,
            )
            .trim()
            .required('Required field')
        : yup
            .string()
            .max(lengthField.wysiwygMaxLength)
            .test(
              'max-len-text',
              `Max ${lengthField.wysiwygDisplayLimit} characters`,
              (val) => !val || length(val) <= lengthField.wysiwygDisplayLimit,
            )
            .trim();
    case CustomQuestionType.TEXT:
      return isMandatory
        ? yup
            .string()
            .max(customFieldsMaxLength[CustomQuestionType.TEXT])
            .trim()
            .required('Required field')
        : yup
            .string()
            .max(customFieldsMaxLength[CustomQuestionType.TEXT])
            .trim();
    case CustomQuestionType.SINGLE_CHOICE:
    case CustomQuestionType.MULTIPLE_CHOICE:
    case CustomQuestionType.NUMBER:
    case CustomQuestionType.ATTACHMENT:
    default:
      return isMandatory
        ? yup.string().trim().required('Required field')
        : yup.string().trim();
  }
};

export const handleSubmitCustomForm = async ({
  questions,
  values,
  tenantId,
  audience,
  responderId,
}: {
  questions: CustomQuestion[];
  values: Record<string, any>;
  tenantId: TenantId;
  audience: Audience;
  responderId: string;
}) => {
  const questionsWithNewResponses = questions.map((question) => {
    if (
      (question.type === CustomQuestionType.MULTIPLE_CHOICE ||
        question.type === CustomQuestionType.SINGLE_CHOICE) &&
      question.options?.length
    ) {
      const value = values[question.id];
      const selectedValues = typeof value === 'string' ? value?.split(',') : [];
      const newOptions = question.options.map((option) => {
        const isSelected = selectedValues.includes(option.id.toString());
        if (!isSelected && question.type === CustomQuestionType.SINGLE_CHOICE) {
          return {
            ...option,
            optionResponse: null,
          };
        } else {
          const oldOptionResponse = option.optionResponse || {};
          const newOptionResponse = {
            questionId: question.id,
            optionId: option.id,
            responderId: responderId,
            value: isSelected ? 'selected' : 'unselected',
          };
          return {
            ...option,
            optionResponse: {
              ...oldOptionResponse,
              ...newOptionResponse,
            },
          };
        }
      });
      return {
        ...question,
        options: newOptions,
      };
    } else if (question.type === CustomQuestionType.ATTACHMENT) {
      const preValue = values[question.id];
      // Value is already JSON string from form
      const value = typeof preValue === 'string' ? preValue.trim() : preValue;
      if (!value) {
        return {
          ...question,
          response: null,
        };
      }
      const oldResponse = question.response || {};
      const newResponse = {
        questionId: question.id,
        responderId: responderId,
        value,
      };
      return {
        ...question,
        response: {
          ...oldResponse,
          ...newResponse,
        },
      };
    } else {
      const preValue = values[question.id];
      const value = typeof preValue === 'string' ? preValue.trim() : preValue;
      if (!value) {
        return {
          ...question,
          response: null,
        };
      }
      const oldResponse = question.response || {};
      const newResponse = {
        questionId: question.id,
        responderId: responderId,
        value,
      };
      return {
        ...question,
        response: {
          ...oldResponse,
          ...newResponse,
        },
      };
    }
  });
  const newRepondersCustomQuestions: RespondersCustomQuestions = {
    tenantId,
    audience,
    responderId,
    questions: questionsWithNewResponses,
  };
  const res = await createCustomQuestionResponse(
    tenantId,
    newRepondersCustomQuestions,
  );
  return res;
};
